/**
 * Timeline Slider Carousel Component
 * Extends the Impact theme's carousel functionality for a custom timeline slider
 */

class TimelineSliderCarousel extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this.slides = Array.from(this.querySelectorAll('.timeline-slider__slide'));
    this.track = this.querySelector('.timeline-slider__track');
    this.dots = Array.from(document.querySelectorAll(`[aria-controls="${this.id}"] .timeline-slider__dot`));
    this.progressFill = document.querySelector('.timeline-slider__progress-fill');
    this.prevButton = document.querySelector(`[aria-controls="${this.id}"].timeline-slider__arrow.prev`);
    this.nextButton = document.querySelector(`[aria-controls="${this.id}"].timeline-slider__arrow.next`);
    
    this.currentIndex = 0;
    this.isTransitioning = false;
    
    // Get visible items count from CSS variable
    this.updateVisibleItems();
    
    // Initialize
    this.init();
    
    // Setup event listeners
    this.setupEventListeners();
    
    // Handle window resize
    window.addEventListener('resize', this.debounce(() => {
      this.updateVisibleItems();
      this.updateSlider(false); // Update without animation on resize
    }, 250), { signal: this._abortController.signal });
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  init() {
    // Set initial state
    this.updateSlider(false);
    this.updateProgress();
    this.updateButtons();
    this.updateDots();
  }

  setupEventListeners() {
    const signal = this._abortController.signal;

    // Previous button
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.previous(), { signal });
    }

    // Next button  
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.next(), { signal });
    }

    // Dot navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index), { signal });
    });

    // Swipe support
    if (this.getAttribute('swipeable') === 'true') {
      this.setupSwipeGestures(signal);
    }

    // Keyboard navigation
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.previous();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    }, { signal });
  }

  setupSwipeGestures(signal) {
    let touchStartX = 0;
    let touchEndX = 0;
    const minSwipeDistance = 50;

    this.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true, signal });

    this.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX, minSwipeDistance);
    }, { signal });
  }

  handleSwipe(startX, endX, minDistance) {
    const distance = endX - startX;
    
    if (Math.abs(distance) > minDistance) {
      if (distance > 0) {
        // Swipe right - go to previous
        this.previous();
      } else {
        // Swipe left - go to next
        this.next();
      }
    }
  }

  updateVisibleItems() {
    const style = getComputedStyle(this);
    const visibleItems = style.getPropertyValue('--timeline-slider-visible-items');
    this.visibleCount = parseInt(visibleItems) || 1;
  }

  previous() {
    if (this.isTransitioning || this.currentIndex === 0) return;
    this.goToSlide(this.currentIndex - 1);
  }

  next() {
    const lastSlideIndex = this.slides.length - 1;
    if (this.isTransitioning || this.currentIndex >= lastSlideIndex) return;
    this.goToSlide(this.currentIndex + 1);
  }

  goToSlide(index, animate = true) {
    if (this.isTransitioning) return;
    
    // Clamp index to valid range (0 to last slide index)
    const lastSlideIndex = this.slides.length - 1;
    index = Math.max(0, Math.min(index, lastSlideIndex));
    
    if (index === this.currentIndex) return;
    
    this.currentIndex = index;
    this.updateSlider(animate);
    this.updateProgress();
    this.updateButtons();
    this.updateDots();
    this.updateActiveSlides();
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('timeline-slider:change', {
      bubbles: true,
      detail: { index: this.currentIndex }
    }));
  }

  updateSlider(animate = true) {
    if (!this.track || this.slides.length === 0) return;
    
    if (animate) {
      this.isTransitioning = true;
    }
    
    const slideWidth = this.slides[0].offsetWidth;
    const gap = parseFloat(
      getComputedStyle(this)?.getPropertyValue('--timeline-slider-gap')
    ) * parseFloat(getComputedStyle(document.documentElement).fontSize) || 24;
    
    // Calculate the maximum scroll position
    // This ensures the last 2 slides are always visible when on the last slide
    const maxScrollIndex = Math.max(0, this.slides.length - this.visibleCount);
    
    // Use the smaller of currentIndex or maxScrollIndex for scroll position
    // This means: scroll until you reach the point where last 2 slides are visible, then stop scrolling
    const scrollIndex = Math.min(this.currentIndex, maxScrollIndex);
    
    const offset = -(scrollIndex * (slideWidth + gap));
    
    this.track.style.transform = `translateX(${offset}px)`;
    
    if (animate) {
      // Wait for transition to complete
      setTimeout(() => {
        this.isTransitioning = false;
      }, 500); // Match CSS transition duration
    }
  }

  updateProgress() {
    if (!this.progressFill || this.slides.length === 0) return;
    
    // Calculate progress as percentage based on current slide position
    // When on last slide (index 3 of 4 slides), progress should be 100%
    const lastSlideIndex = this.slides.length * 2;
    const progress = ((this.currentIndex + 1) / lastSlideIndex) * 200;
    
    this.progressFill.style.width = `${progress}%`;
  }

  updateButtons() {
    if (!this.prevButton || !this.nextButton) return;
    
    // Disable/enable previous button
    if (this.currentIndex === 0) {
      this.prevButton.setAttribute('disabled', '');
    } else {
      this.prevButton.removeAttribute('disabled');
    }
    
    // Disable/enable next button
    // Next button should be disabled when the LAST slide is focused (currentIndex === total slides - 1)
    const lastSlideIndex = this.slides.length - 1;
    if (this.currentIndex >= lastSlideIndex) {
      this.nextButton.setAttribute('disabled', '');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  updateDots() {
    if (this.dots.length === 0) return;
    
    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentIndex;
      const isCompleted = index < this.currentIndex;
      
      dot.classList.toggle('is-active', isActive);
      dot.classList.toggle('is-completed', isCompleted);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  updateActiveSlides() {
    const maxScrollIndex = Math.max(0, this.slides.length - this.visibleCount);
    const scrollIndex = Math.min(this.currentIndex, maxScrollIndex);
    
    this.slides.forEach((slide, index) => {
      // A slide is visible if it's in the current viewport based on scroll position
      const isVisible = index >= scrollIndex && index < scrollIndex + this.visibleCount;
      
      // Only the slide at currentIndex is "active" (focused)
      const isActive = index === this.currentIndex;
      
      slide.classList.toggle('is-active', isActive);
      slide.classList.toggle('is-visible', isVisible);
    });
  }

  // Utility: Debounce function
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Register the custom element
if (!window.customElements.get('timeline-slider-carousel')) {
  window.customElements.define('timeline-slider-carousel', TimelineSliderCarousel);
}

/**
 * Page Dots Component (simplified version for timeline slider)
 * Handles dot navigation clicks
 */
class PageDots extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const carouselId = this.getAttribute('aria-controls');
    const carousel = document.getElementById(carouselId);
    
    if (!carousel) return;
    
    const buttons = Array.from(this.querySelectorAll('button[data-index]'));
    
    buttons.forEach((button) => {
      button.addEventListener('click', (e) => {
        const index = parseInt(button.getAttribute('data-index'));
        
        // Trigger the carousel to go to this slide
        if (carousel && typeof carousel.goToSlide === 'function') {
          carousel.goToSlide(index);
        }
      }, { signal: this._abortController.signal });
    });
  }

  disconnectedCallback() {
    this._abortController.abort();
  }
}

if (!window.customElements.get('page-dots')) {
  window.customElements.define('page-dots', PageDots);
}

/**
 * Navigation Button Components
 * These integrate with Impact theme's existing button components
 */
class PrevButton extends HTMLButtonElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const carouselId = this.getAttribute('aria-controls');
    const carousel = document.getElementById(carouselId);
    
    if (carousel) {
      this.addEventListener('click', () => {
        if (typeof carousel.previous === 'function') {
          carousel.previous();
        }
      }, { signal: this._abortController.signal });
    }
  }

  disconnectedCallback() {
    this._abortController?.abort();
  }
}

class NextButton extends HTMLButtonElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const carouselId = this.getAttribute('aria-controls');
    const carousel = document.getElementById(carouselId);
    
    if (carousel) {
      this.addEventListener('click', () => {
        if (typeof carousel.next === 'function') {
          carousel.next();
        }
      }, { signal: this._abortController.signal });
    }
  }

  disconnectedCallback() {
    this._abortController?.abort();
  }
}

// Only register if not already defined (to avoid conflicts with Impact theme)
if (!window.customElements.get('prev-button')) {
  window.customElements.define('prev-button', PrevButton, { extends: 'button' });
}

if (!window.customElements.get('next-button')) {
  window.customElements.define('next-button', NextButton, { extends: 'button' });
}